import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type ChangeEvent, type Ref } from 'react'

import type { ReactInputProps } from '../../types'
import { type InputState, InternalInput } from './internal/InternalInput'
import { InternalInputWrapper } from './internal/InternalInputWrapper'

export function getInputState({
  hasError = false,
  isDisabled = false,
}: {
  isDisabled?: boolean
  hasError?: boolean
}): InputState {
  if (isDisabled) {
    return 'disabled'
  }
  if (hasError) {
    return 'error'
  }
  return 'default'
}

type Props = {
  /**
   * The value to show in the input. If empty, any provided `placeholder` is shown.
   */
  value: string
  /**
   * Callback called when the value in the input changes. As a convenience, the value is provided
   * separately from the change event for use cases that just need the new value.
   */
  onChange: ({
    event,
    value,
  }: {
    event: ChangeEvent<HTMLInputElement>
    value: string
  }) => void
  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState
  /**
   * Provide `true` to visually hide the input label, while still leaving it present in the DOM
   * for screen readers. Ignored if no `label` is provided.
   */
  hideLabel?: boolean
  /**
   * A label to show above the input. If you would like to visually hide it please use
   * `hideLabel` prop to do so. If you do not provide a value here, you must provide your own
   * `<label>` element for the input for accessibility reasons.
   */
  label?: string
  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"`.
   */
  errorMessage?: string
  /**
   * A control to render at the end of the input, internal to the visual border.
   */
  endControl?: React.ReactNode
  /**
   * A React ref to attach to the actual `input` element, e.g. for triggering focus or blur on
   * the input.
   */
  innerRef?: Ref<HTMLInputElement>
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

type DefaultInputProps = Omit<
  ReactInputProps,
  keyof Props | 'disabled' | 'onChange' | 'css' | 'className' | 'style' | 'type'
>

/**
 * A basic input element, for use when you want to collect a generic string. See the other `*Input`
 * components for collecting more specialized user input.
 *
 * Like all inputs, this should be used inside a `<form>` element. You also may want to use
 * the `getInputState` helper.
 */
export function TextInput({
  endControl,
  errorMessage,
  hideLabel = false,
  innerRef,
  label = '',
  onChange,
  overrideRootStyles,
  state = 'default',
  value,
  ...inputProps
}: Props & DefaultInputProps) {
  return (
    <InternalInputWrapper
      errorMessage={errorMessage}
      hideLabel={hideLabel}
      label={label}
      overrideRootStyles={overrideRootStyles}
      state={state}
    >
      {errorMessageId => (
        <InternalInput
          aria-describedby={errorMessageId}
          endControl={endControl}
          innerRef={innerRef}
          onChange={onChange}
          state={state}
          type="text"
          value={value}
          {...inputProps}
        />
      )}
    </InternalInputWrapper>
  )
}
